
import {defineComponent} from "vue";

//@ts-ignore
import InputMask from "inputmask";
import {vMaska} from "maska";

interface InputMaskConfiguration {
    regex: string | null;
    placeholder: string | null;
}

export default defineComponent({
    name: "TextField",
    directives: {
        maska: vMaska
    },
    props: {
        modelValue: {
            type: String,
            required: true
        },
        inputId: {
            type: String,
            required: true
        },
        labelId: {
            type: String,
            required: true
        },
        label: {
            type: String,
            required: true
        },
        mask: {
            type: String,
            required: false,
            default: undefined
        },
        errorId: {
            type: String,
            default: undefined
        },
        errorMessage: {
            type: String,
            default: undefined
        },
        helperId: {
            type: String,
            default: undefined
        },
        helperText: {
            type: String,
            default: undefined
        },
        isInvalid: {
            type: Boolean,
            default: false
        },
        minLength: {
            type: Number,
            default: undefined
        },
        maxLength: {
            type: Number,
            default: undefined
        },
        readonly: {
            type: Boolean,
            default: false
        },
        isDisabled: {
            type: Boolean,
            default: false
        },
        isRequired: {
            type: Boolean,
            default: false
        },
        isHidden: {
            type: Boolean,
            default: false
        },
        requiredLabelSuffix: {
            type: String,
            required: false,
            default: " <span class='text-primary'>*</span><span class='visually-hidden'>obligatoire</span>"
        },
        anonymized: {
            type: Boolean,
            default: false
        },
        autocomplete: {
            type: String,
            default: undefined
        },
        typeValue: {
            type: String,
            default: "text"
        },
        inputModeValue: {
            type: String,
            default: undefined,
            validator: (value: string) => {
                return ["text", "none", "tel", "url", "email", "numeric", "decimal", "search", undefined].includes(value);
            }
        },
        inputMaskRegex: {
            type: String,
            required: false,
            default: undefined
        },
        inputMaskPlaceholder: {
            type: String,
            required: false,
            default: undefined
        },
        isFullWidthInput: {
            type: Boolean,
            required: false,
            default: false
        },
        isTextUppercaseInput: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        maskaData(): string {
            if (!this.inputMaskRegex && this.mask) {
                // Activation du mask par Maska seulement si aucun inputMaskRegex n'est déclaré
                return this.mask;
            }
            return undefined;
        },
        inputClassObject(): Record<string, boolean> {
            return {
                "form-control": true,
                "form-control-empty": this.modelValue === "",
                "is-invalid": this.isInvalid,
                "ob1-form-control-fullwidth": this.isFullWidthInput,
                "text-uppercase": this.isTextUppercaseInput
            };
        },
        inputMaskConfiguration(): InputMaskConfiguration {
            return {
                regex: this.inputMaskRegex,
                placeholder: this.inputMaskPlaceholder
            }
        },
        labelWithPossibleRequiredSuffix(): string {
            if (this.isRequired && this.requiredLabelSuffix) {
                return this.label + this.requiredLabelSuffix;
            }
            return this.label;
        },
        ariaDescribedBy(): string {
            if (this.helperId && this.helperText) {
                return this.helperId;
            }
            return undefined;
        }
    },
    watch: {
        inputMaskConfiguration: {
            immediate: true,
            handler(_newValue: InputMaskConfiguration | null, _oldValue: InputMaskConfiguration | null) {
                this.$nextTick(() => {
                    this.applyInputMaskConfiguration();
                });
            }
        }
    },
    methods: {
        onBlur(): void {
            this.$emit("blur", this.modelValue);
        },
        onInput(event: Event): void {
            this.$emit("change", (event.target as HTMLInputElement).value);
        },
        onPaste(event: Event): void {
            this.$emit("paste", event);
        },
        applyInputMaskConfiguration(): void {
            const textField = this.$refs.textFieldInputRef;
            if (textField) {
                if (textField.inputmask) {
                    // Supprime l'ancien inputMask
                    InputMask.remove(textField);
                }
                if (this.inputMaskRegex) {
                    InputMask({
                        regex: this.inputMaskConfiguration.regex,
                        placeholder: this.inputMaskConfiguration.placeholder,
                    }).mask(textField);
                }
            }
        }
    }
});
